import { TableRow, TableCell, Typography } from '@mui/material';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import { hasAnyDifference, isDifferentFromBase } from '../helpers';
import { DigitalFunnelSectionProps } from './types';

export default function DigitalFunnelSection({
  touchpointName,
  funnelName,
  plans,
  formatBudget,
  showOnlyDifferences,
  funnelAggs,
  getPriorityLabel,
}: DigitalFunnelSectionProps) {
  const funnelBudgetValues = funnelAggs.map((agg) => agg?.budget?.value);
  const funnelPriorities = funnelAggs.map((agg) =>
    agg ? getPriorityLabel(agg) : 'N/A'
  );
  const funnelImpressions = funnelAggs.map((agg) => agg?.impressions);
  const funnelReach = funnelAggs.map((agg) => agg?.reach);
  const funnelClicks = funnelAggs.map((agg) => agg?.clicks);
  const funnelVideoViews = funnelAggs.map((agg) => agg?.videoViews);

  const hasBudgetDiff = hasAnyDifference<number | undefined>(
    funnelBudgetValues
  );
  const hasPrioDiff = hasAnyDifference<string>(funnelPriorities);
  const hasImpDiff = hasAnyDifference<number | undefined>(funnelImpressions);
  const hasReachDiff = hasAnyDifference<number | undefined>(funnelReach);
  const hasClicksDiff = hasAnyDifference<number | undefined>(funnelClicks);
  const hasVvDiff = hasAnyDifference<number | undefined>(funnelVideoViews);

  return (
    <>
      {/* Funnel budget row */}
      {(!showOnlyDifferences || hasBudgetDiff) && (
        <TableRow
          sx={{
            height: '60px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            <Typography fontWeight={700} fontSize="14px">
              {funnelName}
            </Typography>
          </TableCell>
          {plans.map((plan, index) => {
            const agg = funnelAggs[index];
            const diff = isDifferentFromBase<number | undefined>(
              funnelBudgetValues,
              index
            );

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-budget-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 700,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {agg?.budget
                  ? formatBudget(agg.budget.value, agg.budget.currency)
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Priority row */}
      {(!showOnlyDifferences || hasPrioDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '14px',
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Priority
          </TableCell>
          {plans.map((plan, index) => {
            const text = funnelPriorities[index];
            const diff = isDifferentFromBase<string>(funnelPriorities, index);

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-prio-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {text}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Impressions */}
      {(!showOnlyDifferences || hasImpDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '14px',
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Impressions
          </TableCell>
          {plans.map((plan, index) => {
            const value = funnelImpressions[index];
            const diff = isDifferentFromBase<number | undefined>(
              funnelImpressions,
              index
            );

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-imp-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null
                  ? formatNumberWithSpaces(value.toFixed(0))
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Reach */}
      {(!showOnlyDifferences || hasReachDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '14px',
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Reach
          </TableCell>
          {plans.map((plan, index) => {
            const value = funnelReach[index];
            const diff = isDifferentFromBase<number | undefined>(
              funnelReach,
              index
            );

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-reach-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null
                  ? formatNumberWithSpaces(value.toFixed(0))
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Clicks */}
      {(!showOnlyDifferences || hasClicksDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '14px',
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Clicks
          </TableCell>
          {plans.map((plan, index) => {
            const value = funnelClicks[index];
            const diff = isDifferentFromBase<number | undefined>(
              funnelClicks,
              index
            );

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-clicks-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null
                  ? formatNumberWithSpaces(value.toFixed(0))
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}

      {/* Video views */}
      {(!showOnlyDifferences || hasVvDiff) && (
        <TableRow
          sx={{
            height: '40px',
            border: '1px solid',
            borderColor: '#C7D8F9',
          }}
        >
          <TableCell
            sx={{
              pl: 4,
              fontWeight: 400,
              fontSize: '14px',
              border: '1px solid',
              borderColor: '#C7D8F9',
            }}
          >
            Video views
          </TableCell>
          {plans.map((plan, index) => {
            const value = funnelVideoViews[index];
            const diff = isDifferentFromBase<number | undefined>(
              funnelVideoViews,
              index
            );

            return (
              <TableCell
                key={`${touchpointName}-${funnelName}-vv-${plan.id}`}
                sx={{
                  borderLeft: '1px solid',
                  borderColor: '#C7D8F9',
                  fontWeight: 400,
                  fontSize: '14px',
                  textAlign: 'center',
                  verticalAlign: 'center',
                  color: diff ? 'error.main' : '#525F81',
                }}
              >
                {value != null
                  ? formatNumberWithSpaces(value.toFixed(0))
                  : 'N/A'}
              </TableCell>
            );
          })}
        </TableRow>
      )}
    </>
  );
}
